import { Box, Divider, Flex } from '@chakra-ui/react';
import DashboardCard from '@components/atoms/DashboardCard';
import TextLink from '@components/interactive/TextLink';
import Body1 from '@components/typography/Body1';
import Header from '@components/typography/Header';
import { Routes } from '@constants/routes';
import useWindowDimensions from '@hooks/useWindowDimensions';
import providersIcon from '@public/flaticons/doctor.png';
import pharmacyIcon from '@public/flaticons/medicine.svg';
import prescriptionIcon from '@public/flaticons/pill-bottle.png';
import { formatPrescription } from '@utils/prescriptions';
import Image from 'next/image';
import { titleCase } from 'title-case';

function HealthcareSummary({ user }) {
  const { isMobile } = useWindowDimensions();

  const providers = user.providers.map((el) => {
    const humanProvider = el.first_name && el.last_name;
    const name = humanProvider
      ? titleCase(`${el.first_name?.toLowerCase()} ${el.last_name?.toLowerCase()}`)
      : titleCase(el.organization_name?.toLowerCase());
    return (
      <Flex key={el.id} gap={3}>
        <Body1 textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
          {' '}
          {name}
        </Body1>
        {el.provider_role && <Body1>({titleCase(el.provider_role.toLowerCase())})</Body1>}
      </Flex>
    );
  });

  const prescriptions = user.prescriptions.map((el) => (
    <Box key={el.id} mt={3}>
      <Body1 textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
        {formatPrescription({ prescription: el, showName: true })}
      </Body1>
    </Box>
  ));

  const pharmacies = user.pharmacies.map((el) => (
    <Box key={el.id}>
      <Body1 bold>{el.name}</Body1>{' '}
      <Body1 textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
        {el.address_line_one} {el.address_line_two} {el.city} {el.state}
      </Body1>
    </Box>
  ));

  const renderSectionTitle = ({ title, src, alt }) => (
    <Flex mb={2} alignItems="center" gap={4}>
      <Image src={src} height={30} width={30} alt={alt} />
      <Body1 bold display="inline-block">
        {title}
      </Body1>
    </Flex>
  );

  return (
    <DashboardCard id="healthcare_summary_card">
      <Box display="flex" flexDirection="column" width="100%">
        <Header as="h4" mb={2}>
          My Healthcare Snapshot
        </Header>
        <Body1 mb={6}>Keep this up-to-date so we can ensure you're completely covered.</Body1>
        <Flex alignItems="center" justifyContent="space-between">
          {renderSectionTitle({
            title: 'Providers',
            src: providersIcon,
            alt: 'Providers Icon',
          })}
          <TextLink
            text={isMobile ? 'Edit' : `${providers.length ? 'Edit' : 'Add'} Providers`}
            href={Routes.accountSurveyUpdateProviders}
          />
        </Flex>
        {providers.length ? <>{providers}</> : <Body1>None</Body1>}
        <Divider margin="1rem 0" />
        <Flex alignItems="center" justifyContent="space-between">
          {renderSectionTitle({
            title: 'Prescriptions',
            src: prescriptionIcon,
            alt: 'Prescription Icon',
          })}
          <TextLink
            text={isMobile ? 'Edit' : `${prescriptions.length ? 'Edit' : 'Add'} Prescriptions`}
            href={Routes.accountSurveyUpdatePrescriptions}
          />
        </Flex>
        {prescriptions.length ? <>{prescriptions}</> : <Body1>None</Body1>}
        <Divider margin="1rem 0" />
        <Flex alignItems="center" justifyContent="space-between">
          {renderSectionTitle({
            title: 'Pharmacies',
            src: pharmacyIcon,
            alt: 'Pharmacy Icon',
          })}
          <TextLink
            text={isMobile ? 'Edit' : `${pharmacies.length ? 'Edit' : 'Add'} Pharmacies`}
            href={Routes.accountSurveyUpdatePharmacies}
          />
        </Flex>
        {pharmacies.length ? <>{pharmacies}</> : <Body1>None</Body1>}
      </Box>
    </DashboardCard>
  );
}

export default HealthcareSummary;
